import type { FC } from 'react';
import { useState } from 'react';
import { Flex, Link } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import {
    getTodayStoreHoliday,
    getTodaysStoreClosingTime,
    getTodaysStoreOpeningTime,
    getTomorrowStoreOpeningTime,
    isStoreOpen,
} from '../../bff-api/utils/store-utils';
import { Button, Text } from '../../ui-components';
import { ChevronDownIcon, ChevronUpIcon } from '../icons';
import { StoreHours } from '../store-hours';
import type { Store as StoreModel } from '../../bff-api/stores/model';

interface Props {
    id: string;
    title?: string;
    store?: StoreModel;
}

export const Store: FC<Props> = ({ id, title, store }: Props) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!store?.id) {
        return null;
    }

    const isOpenStore = isStoreOpen(store);
    const tomorrowOpeningTime = getTomorrowStoreOpeningTime(store, { infix: ' ' }).toUpperCase();
    const todaysOpeningTime = getTodaysStoreOpeningTime(store, { infix: ' ' }).toUpperCase();
    const todaysClosingTime = getTodaysStoreClosingTime(store, { infix: ' ' }).toUpperCase();
    const todaysHoliday = getTodayStoreHoliday(store);

    return (
        <Flex data-testid={`${id}-store-${store.id}`} direction="column">
            {title && (
                <Text id={`${id}-store-${store.id}-title`} size="large" style="bold">
                    {title}
                </Text>
            )}

            <Flex direction="column">
                <Flex justifyContent="space-between">
                    <Text id={`${id}-store-${store.id}-name`} style="bold">
                        {store.name}
                    </Text>

                    <Link
                        data-testid={`${id}-store-${store.id}-view-details-link`}
                        href={store.url}
                        _hover={{ textDecoration: 'none' }}
                    >
                        <Text type="link" size="small">
                            <FormattedMessage id="view.details" defaultMessage="View details" />
                        </Text>
                    </Link>
                </Flex>

                <Text id={`${id}-store-${store.id}-address`} size="small">
                    {store.address1}
                    {store.address2 ? `, ${store.address2}` : null}
                </Text>

                <Text id={`${id}-store-${store.id}-city_state_postalCode`} size="small">
                    {store.city}
                    {store.stateCode && store.postalCode ? (
                        <>
                            {', '}
                            {store.stateCode} {store.postalCode}
                        </>
                    ) : null}
                </Text>

                <Flex gap={1} marginY={2}>
                    {isOpenStore ? (
                        <>
                            <Text id={`${id}-store-${store.id}-open-label`} size="small">
                                <FormattedMessage
                                    id="store.item.today.open.until"
                                    defaultMessage="Today open until"
                                />
                                :
                            </Text>

                            <Text
                                id={`${id}-store-${store.id}-open-value`}
                                size="small"
                                color="slt-success-green.1000"
                            >
                                {todaysClosingTime}
                            </Text>
                        </>
                    ) : null}

                    {!isOpenStore && todaysOpeningTime ? (
                        <>
                            <Text id={`${id}-store-${store.id}-open-today-label`} size="small">
                                <FormattedMessage
                                    id="store.item.open.today.at"
                                    defaultMessage="Open today at"
                                />
                                :
                            </Text>

                            <Text
                                id={`${id}-store-${store.id}-open-today-value`}
                                size="small"
                                color="slt-success-green.1000"
                            >
                                {todaysOpeningTime}
                            </Text>
                        </>
                    ) : null}

                    {!isOpenStore && !todaysOpeningTime && tomorrowOpeningTime ? (
                        <>
                            <Text id={`${id}-store-${store.id}-closed-today-label`} size="small">
                                <FormattedMessage
                                    id="store.item.closed.today.until"
                                    defaultMessage="Closed today until"
                                />
                                :
                            </Text>

                            <Text
                                id={`${id}-store-${store.id}-closed-today-value`}
                                size="small"
                                color="slt-red-error"
                            >
                                {tomorrowOpeningTime}
                            </Text>
                        </>
                    ) : null}

                    {!isOpenStore && !todaysOpeningTime && !tomorrowOpeningTime ? (
                        <>
                            <Text id={`${id}-store-${store.id}-closed-label`} size="small">
                                <FormattedMessage id="store.item.today" defaultMessage="Today" />:
                            </Text>

                            <Text
                                id={`${id}-store-${store.id}-closed-value`}
                                size="small"
                                color="slt-red-error"
                            >
                                <FormattedMessage
                                    id="store.item.closed.holiday"
                                    defaultMessage="closed{holiday}"
                                    values={{
                                        holiday: todaysHoliday ? ` (${todaysHoliday.label})` : '',
                                    }}
                                />
                            </Text>
                        </>
                    ) : null}
                </Flex>
            </Flex>

            <Button
                id={`${id}-store-${store.id}-store-hours-button`}
                variant="tertiary"
                textSize="small"
                padding={0}
                onClick={() => setIsOpen(!isOpen)}
                className="mb-2 !justify-start"
            >
                <Flex gap={1} alignItems="center">
                    <FormattedMessage id="store.hours" defaultMessage="Store hours" />

                    {isOpen ? (
                        <ChevronUpIcon width={6} height={6} />
                    ) : (
                        <ChevronDownIcon width={6} height={6} />
                    )}
                </Flex>
            </Button>

            {isOpen && <StoreHours id={`${id}-store-${store.id}-hours`} store={store} showPhone />}
        </Flex>
    );
};

Store.defaultProps = {
    title: undefined,
    store: undefined,
};
